import dayjs from 'dayjs'
import React from 'react'
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material'
import ReportTypeMarker from 'src/consumer/components/LiveSessionPage/components/LSPastVisitReportsComponent/ReportTypeMarker'

/**
 * Timeline-style date marker for visit history. Visit + date on first row (left column),
 * time/type cards below in the same left column, report content on the right.
 * timeMarkers: array of { time, label } (one per report, same order as children).
 */
const TIMELINE_LEFT_WIDTH = 120
const TIMELINE_RIGHT_PADDING = 16
const HISTORY_PRESCRIPTION_A4_WIDTH = 794
// A4 width + the 12px left indent the report container adds — the natural box the report fills.
const REPORT_FIT_WIDTH = HISTORY_PRESCRIPTION_A4_WIDTH + 12
const MIN_FIT_SCALE = 0.3

export default function ConnectingLines({ date, visitNo, timeMarkers = [], children, forceStacked = false }) {
  const theme = useTheme()
  // forceStacked lets a desktop host (e.g. the chamber Patient History tab) opt into the mobile
  // stacked layout — drops the timeline rail + vertical line and stacks reports full-width.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')) || forceStacked
  const dateStr = date?.split(',')[0] || ''
  const currentDate = dayjs(dateStr, 'DD/MM/YYYY')
  const dayNum = currentDate.format('D')
  const monthShort = currentDate.format('MMM')
  const weekday = currentDate.format('ddd')
  const isValid = currentDate.isValid()

  const childArray = React.Children.toArray(children)

  // Mobile: render each report at true A4 width and uniformly scale it to fit the available
  // width (faithful "PDF thumbnail" — nothing reflows, so the letterhead header/footer don't
  // distort). Measured from the wrapper so it tracks the drawer/orientation.
  const fitWrapperRef = React.useRef(null)
  const [fitScale, setFitScale] = React.useState(() => {
    if (typeof window === 'undefined') return 1
    return Math.min(1, Math.max(MIN_FIT_SCALE, (window.innerWidth - 24) / REPORT_FIT_WIDTH))
  })
  React.useEffect(() => {
    if (!isMobile) return undefined
    const el = fitWrapperRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const update = () => {
      const w = el.clientWidth
      if (w > 0) {
        setFitScale(Math.min(1, Math.max(MIN_FIT_SCALE, w / REPORT_FIT_WIDTH)))
      }
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [isMobile])

  // Mobile: drop the timeline rail + vertical line. The narrow left column squeezed the A4
  // reports into an unreadable sliver, so stack instead — Visit + date header on top, then each
  // report full-width with its time/type marker directly above it.
  if (isMobile) {
    return (
      <Box ref={fitWrapperRef} className="d-flex flex-column" sx={{ width: '100%', maxWidth: '100%', mx: 'auto', py: 1, pb: 3 }}>
        <Box
          className="hide-on-print-only"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}
        >
          <Typography
            component="span"
            variant="caption"
            sx={{
              px: 1.25,
              py: 0.375,
              borderRadius: 10,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.02em',
            }}
          >
            Visit {visitNo}
          </Typography>
          {isValid ? (
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {dayNum} {monthShort}
              <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.75 }}>
                {weekday}
              </Typography>
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {date}
            </Typography>
          )}
        </Box>
        {childArray.map((child, idx) => (
          <Box key={idx} sx={{ width: '100%', minWidth: 0, mb: 2.5 }}>
            {timeMarkers[idx] && (
              <Box className="hide-on-print-only" sx={{ mb: 1 }}>
                <ReportTypeMarker inline time={timeMarkers[idx].time} label={timeMarkers[idx].label} />
              </Box>
            )}
            {/* True A4 width, scaled to fit. zoom shrinks the layout box too (no empty gap below),
                and print clones the inner element so printed output stays full-size. */}
            <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
              <div style={{ width: `${REPORT_FIT_WIDTH}px`, zoom: fitScale }}>{child}</div>
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  return (
    <Box
      className="d-flex flex-column"
      sx={{
        position: 'relative',
        // Include timeline column + its right padding + A4 report width,
        // so the report pane can still render full 794px on wide screens.
        maxWidth: TIMELINE_LEFT_WIDTH + TIMELINE_RIGHT_PADDING + HISTORY_PRESCRIPTION_A4_WIDTH,
        width: '100%',
        mx: 'auto',
        py: 1,
        pb: 3,
      }}
    >
      {/* Colorful vertical line at horizontal center of date/time cards, behind the cards */}
      <Box
        className="hide-on-print-only"
        sx={{
          position: 'absolute',
          left: TIMELINE_LEFT_WIDTH / 2 - 1,
          top: 0,
          bottom: 0,
          width: 3,
          background: (theme) =>
            `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark ?? theme.palette.primary.main} 100%)`,
          borderRadius: 1,
          zIndex: 0,
        }}
      />
      {/* Row 1: Visit + Date (left), empty spacer (right) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
        }}
      >
        <Box
          className="hide-on-print-only"
          sx={{
            width: TIMELINE_LEFT_WIDTH,
            flexShrink: 0,
            pr: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography
            component="span"
            variant="caption"
            sx={{
              display: 'inline-block',
              px: 1.25,
              py: 0.375,
              borderRadius: 10,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.02em',
            }}
          >
            Visit {visitNo}
          </Typography>
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              mt: 1,
              textAlign: 'center',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            {isValid ? (
              <>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.1,
                    color: 'text.primary',
                    pt: 1,
                    px: 1.25,
                  }}
                >
                  {dayNum}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    pb: 0.5,
                  }}
                >
                  {monthShort}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                    pb: 0.75,
                  }}
                >
                  {weekday}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
                {date}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1, minWidth: 0, minHeight: 60 }} />
      </Box>
      {/* Rows: time card (left) | report content (right) */}
      {childArray.map((child, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
          }}
        >
          <Box
            className="hide-on-print-only"
            sx={{
              width: TIMELINE_LEFT_WIDTH,
              flexShrink: 0,
              pr: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {timeMarkers[idx] && (
              <ReportTypeMarker
                inline
                time={timeMarkers[idx].time}
                label={timeMarkers[idx].label}
              />
            )}
          </Box>
          <Box sx={{ position: 'relative', zIndex: 1, flex: 1, minWidth: 0 }}>{child}</Box>
        </Box>
      ))}
    </Box>
  )
}
